import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, Link, Stack, Table, Typography } from '@mui/joy';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { directSupabase } from '../lib/supabaseClient';

type Finding = {
  id: string;
  survey_id: string;
  project_id: string;
  lifecycle_ref: string | null;
  sheet_count: number;
  sent_count: number;
  duplicate_count: number;
  extra_in_sheet: number;
  missing_from_sheet: number;
  first_detected_at: string;
  last_checked_at: string;
  resolved_at: string | null;
};

const MIGRATION_MISSING = /survey_integrity_findings|run_first_survey_integrity_check|schema cache|does not exist/i;

function when(value: string | null): string {
  return value ? format(new Date(value), 'd MMM yyyy, HH:mm') : '';
}

function describe(finding: Finding): string {
  const parts: string[] = [];
  if (finding.duplicate_count > 0) parts.push(`${finding.duplicate_count} duplicated`);
  if (finding.extra_in_sheet > 0) parts.push(`${finding.extra_in_sheet} not in what was sent`);
  if (finding.missing_from_sheet > 0) parts.push(`${finding.missing_from_sheet} missing from the sheet`);
  return parts.join(', ');
}

const SurveyIntegrityPanel: React.FC = () => {
  const navigate = useNavigate();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationMissing, setMigrationMissing] = useState(false);
  const [lastRun, setLastRun] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadErr } = await directSupabase
      .from('survey_integrity_findings')
      .select('*')
      .order('first_detected_at', { ascending: false })
      .limit(100);
    if (loadErr) {
      if (MIGRATION_MISSING.test(loadErr.message)) setMigrationMissing(true);
      else setError(loadErr.message);
      setFindings([]);
    } else {
      setMigrationMissing(false);
      setFindings((data || []) as Finding[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError(null);
    const { data, error: runErr } = await directSupabase.rpc('run_first_survey_integrity_check');
    if (runErr) {
      if (MIGRATION_MISSING.test(runErr.message)) setMigrationMissing(true);
      else setError(runErr.message);
    } else {
      setLastRun(`Check finished: ${Number(data || 0)} open finding${Number(data || 0) === 1 ? '' : 's'}.`);
      await load();
    }
    setRunning(false);
  };

  const open = findings.filter((f) => !f.resolved_at);
  const resolved = findings.filter((f) => !!f.resolved_at);

  const renderTable = (rows: Finding[], showResolved: boolean) => (
    <Table size="sm" stripe="odd" sx={{ '& td, & th': { verticalAlign: 'top' } }}>
      <thead>
        <tr>
          <th style={{ width: 120 }}>Job</th>
          <th style={{ width: 110 }}>On the sheet</th>
          <th style={{ width: 110 }}>Sent</th>
          <th>Difference</th>
          <th style={{ width: 170 }}>First detected</th>
          <th style={{ width: 170 }}>{showResolved ? 'Resolved' : 'Last checked'}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((finding) => (
          <tr key={finding.id}>
            <td>
              <Link
                component="button"
                onClick={() => navigate(`/projects/${encodeURIComponent(finding.project_id)}/workspace/quotes`)}
              >
                {finding.lifecycle_ref || 'Open job'}
              </Link>
            </td>
            <td>{finding.sheet_count}</td>
            <td>{finding.sent_count}</td>
            <td>{describe(finding)}</td>
            <td>{when(finding.first_detected_at)}</td>
            <td>{when(showResolved ? finding.resolved_at : finding.last_checked_at)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography level="title-lg">First Survey integrity</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              Compares every sent First Survey sheet with the designs the customer was sent. Runs every hour in the background.
            </Typography>
          </Box>
          <Button onClick={runNow} loading={running} disabled={migrationMissing}>
            Run check now
          </Button>
        </Stack>
      </Card>

      {migrationMissing && (
        <Alert color="warning">
          The survey integrity migration has not been applied yet (20260928120000-first-survey-sheet-integrity.sql).
        </Alert>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {lastRun && <Alert color="neutral">{lastRun}</Alert>}

      {!migrationMissing && !loading && (
        <Card variant="outlined">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography level="title-md">Open findings</Typography>
            <Chip size="sm" color={open.length ? 'danger' : 'success'} variant="soft">
              {open.length}
            </Chip>
          </Stack>
          {open.length
            ? renderTable(open, false)
            : <Typography level="body-sm">Every sent First Survey matches what the customer was sent.</Typography>}
        </Card>
      )}

      {!migrationMissing && !loading && resolved.length > 0 && (
        <Card variant="outlined">
          <Typography level="title-md" sx={{ mb: 1 }}>Resolved</Typography>
          {renderTable(resolved, true)}
        </Card>
      )}
    </Stack>
  );
};

export default SurveyIntegrityPanel;
